import React, { useRef, useContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FormFieldWrapper from 'mrf/view/FormFieldWrapper';
import { StyledFileContainer, StyledFileLabel, StyledActionIcon } from 'styles/components/FileField.styles';
import { FormContext } from './FormCRUD';
import { saveAs } from 'file-saver';
import { daoGet, defaultAlertResponseLength } from 'mrf/Dao';
import { useTranslation } from 'react-i18next';
import NonIdFieldPropType from 'mrf/utils/NonIdFieldPropType';
import { generateHtmlId } from 'mrf/utils/StringGenerators';
import { useFormFullSelector } from 'mrf/redux/ReduxHelper';

import T from 'mrf/utils/T';
import Hidden from 'styles/basic/Hidden';
import Tooltip from 'mrf/view/Tooltip';
import notification, { TYPE } from 'mrf/utils/notification';
import Flex from 'mrf/layout/Flex';
import If from "mrf/utils/If";

FileField.propTypes = {
    context: PropTypes.object,
    label: PropTypes.string,
    field: NonIdFieldPropType, // Field name.
    placeholder: PropTypes.string,
    icon: PropTypes.elementType,
    htmlId: PropTypes.string,
    maxSize: PropTypes.number,
};

export const asyncFileReader = file => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve({
            name: file.name,
            blob: reader.result,
        });

        reader.onerror = reject;

        reader.readAsDataURL(file);
    });
}

export default function FileField({
    context,
    field,
    placeholder,
    icon: Icon = 'div',
    htmlId,
    label,
    maxSize,
    ...props
}) {
    const [fileName, setFileName] = useState();
    const { t } = useTranslation();

    const formContext = useContext(FormContext);
    if (!context) {
        context = formContext;
    }

    const refDrop = useRef(null);

    const { data, set } = useFormFullSelector(context)

    const fieldData = data?.[field];
    const fieldId = data?.[field + 'Id'];

    htmlId = htmlId ?? generateHtmlId(context.resource ?? 'FormField', field);

    const readOnly = context?.readOnly;

    useEffect(() => {
        async function fetchData() {
            if (!fieldData?.name && fieldId) {
                try {
                    const file = await daoGet(`/api/files/${fieldId}/name`);
                    setFileName(file.name ?? 'NO_NAME_FILE');
                } catch (e) {
                    notification({ type: TYPE.ERROR, message: "Unable to get document file name." });
                }
            }
        }
        fetchData();
    }, [fieldId]); // eslint-disable-line

    const handleChange = async e => {
        if (e.target.files[0].size > (maxSize ?? defaultAlertResponseLength << 20)) { //20mb
            notification({ type: TYPE.ERROR, message: <>The selected file is too big!</> });
            return;
        }

        e.preventDefault?.();
        e.stopPropagation?.();
        const file = await asyncFileReader(e.target.files[0]);

        set({
            [field]: {
                name: file.name,
                blob: file.blob,
            },
        });
    }

    const handleDownload = useCallback(
        async e => {
            e.preventDefault?.();
            e.stopPropagation?.();

            try {
                let fullFile = fieldData?.blob ? fieldData : await daoGet(`/api/files/${fieldId}`);
                saveAs(fullFile.blob, fullFile.name);
            } catch (e2) {
                alert(t('An unexpected error has occurred!'));
                console.error(e2);
            }
        },
        [fieldData, fieldId, t]
    );

    const FileWrapper = useCallback(
        () => <>
            <If condition={readOnly && fileName}>
                <Flex>
                    <p>{fileName}</p>
                    <Tooltip content={<>Download {{ fileName }}</>}>
                        <StyledActionIcon onClick={handleDownload} />
                    </Tooltip>
                </Flex>
            </If>
            <If condition={!readOnly}>
                <StyledFileContainer htmlFor={htmlId} ref={refDrop} {...props}>
                    <Icon size='25' />
                    <StyledFileLabel>
                        <T>{placeholder}</T>
                    </StyledFileLabel>
                    <If condition={!!fileName}>
                        <Tooltip content={<>Download {{ fileName }}</>}>
                            <StyledActionIcon onClick={handleDownload} />
                        </Tooltip>
                    </If>
                </StyledFileContainer>
            </If>
        </>,
        [fileName, handleDownload, htmlId, placeholder, props, readOnly]
    );

    return (<>
        <Hidden
            as='input'
            type='file'
            onChange={handleChange}
            id={htmlId}
            {...props}
        />
        <FormFieldWrapper
            context={context}
            label={label}
            field={field}
            alwaysUndefinedValue={true}
        >
            <FileWrapper />
        </FormFieldWrapper>
    </>);
}
